import { useEffect, useState, useCallback } from 'react';
import Select from 'react-select';
import { supabase } from '../services/supabase';
import VendorEditModal from './VendorEditModal';

const NEW_VENDOR_OPTION = { value: '__new__', label: '+ New vendor…', isNewAction: true };

const VENDOR_FIELDS = `
  id, name, vendor_code, contact_name, contact_email, contact_phone,
  address, city, state, zip, payment_terms, default_ship_via, notes, is_active
`;

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '34px',
    borderRadius: '2px',
    borderColor: state.isFocused ? '#94a3b8' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 1px #94a3b8' : 'none',
    fontSize: '0.875rem',
    '&:hover': { borderColor: '#94a3b8' },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    backgroundColor: state.isSelected
      ? '#334155'
      : state.isFocused
      ? '#f1f5f9'
      : 'white',
    color: state.isSelected
      ? 'white'
      : state.data.isNewAction
      ? '#0369a1'
      : '#0f172a',
    cursor: 'pointer',
  }),
  menu: (base) => ({ ...base, fontSize: '0.875rem', borderRadius: '2px', zIndex: 50 }),
  menuPortal: (base) => ({ ...base, zIndex: 50 }),
};

export default function VendorPicker({
  value,
  onChange,
  placeholder = 'Select vendor…',
  isDisabled = false,
  className = '',
}) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('suppliers')
      .select(VENDOR_FIELDS)
      .eq('is_active', true)
      .order('name');
    if (!error) setVendors(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const options = [
    ...vendors.map((v) => ({
      value: v.id,
      label: v.city
        ? `${v.name} · ${v.city}${v.state ? ', ' + v.state : ''}`
        : v.name,
      vendor: v,
    })),
    NEW_VENDOR_OPTION,
  ];

  const selected = value != null
    ? options.find((o) => o.value === value) || null
    : null;

  const handleChange = (option) => {
    if (option?.isNewAction) {
      setShowModal(true);
      return;
    }
    onChange?.(option?.value ?? null, option?.vendor ?? null);
  };

  return (
    <div className={className}>
      <Select
        value={selected}
        onChange={handleChange}
        options={options}
        isLoading={loading}
        isClearable
        placeholder={placeholder}
        isDisabled={isDisabled}
        styles={selectStyles}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      />
      <VendorEditModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        vendor={null}
        onSaved={async (created) => {
          await fetchVendors();
          onChange?.(created.id, created);
        }}
      />
    </div>
  );
}
